import { Container, Sprite, Ticker } from '../../odie';

import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { tweenPower2In } from '../../lib/util/tweens';
import { ParticleEmitter } from '../particles/ParticleEmitter';
import { getMatch2AssetPath } from '../util/assets';
import gsap from 'gsap';

// types
//-----------------------------------------------------------------------------
export type ComboGlowAnimationOptions = {};

// constants
//-----------------------------------------------------------------------------
const manifest = {
  glow: 'fx.combo/fx.combo.glow.png',
  tingle: 'fx.combo/fx.combo.tingle.png',
};

/*
    combo glowy effect shown under combo candidate blocks
*/
export class ComboGlowAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  // scene
  private _glow!: Sprite;
  private _tingles!: ParticleEmitter;
  // state
  private _started!: boolean;
  // handlers
  private _stepper = (dt: number) => this._stepGlow();

  // init
  //-------------------------------------------------------------------------
  public static assets(): string[] {
    return Object.values(manifest);
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(): Promise<void> {
    // if not started
    if (!this._started) {
      // create the scene once, then reuse it across start/stop cycles
      if (!this._tingles) {
        this._createGlow();
        this._createTingles();
      }

      // set started
      this._started = true;

      // spawn
      this.addChild(this._glow);
      this.addChild(this._tingles.view);

      // start
      Ticker.system.add(this._stepper);
      app.particles.add(this._tingles);
      this._tingles.start();

      this.alpha = 0;
      gsap.to(this, { alpha: 1, duration: 0.3, ease: 'linear' });
    }
  }

  public stop() {
    // if started
    if (this._started) {
      // reset started
      this._started = false;

      // stop
      app.particles.destroy(this._tingles);
      Ticker.system.remove(this._stepper);

      // despawn
      this.removeChild(this._tingles.view);
      this.removeChild(this._glow);
    }
  }

  // private: stppers
  //-------------------------------------------------------------------------
  private _stepGlow() {
    const scale = Math.sin(Ticker.system.lastTime * 0.005) * 0.2 + 2;
    this._glow.scaleX = this._glow.scaleY = scale;
  }

  // private: scene
  //-------------------------------------------------------------------------
  private _createGlow() {
    // create glow
    const glow = (this._glow = Sprite.from(manifest.glow));
    glow.anchorX = glow.anchorY = 0.5;
    glow.scaleX = glow.scaleY = 2.1;
    glow.alpha = 0.75;
  }

  private _createTingles() {
    // create emitter
    this._tingles = app.particles.create({
      textures: [getMatch2AssetPath(manifest.tingle)],
      rate: 0.15,
      limit: 50,
      scale: [
        { x: 1.2, y: 1.2 },
        { x: 2, y: 2 },
      ],
      duration: 5.5,
      behaviors: [
        { type: 'explode', magnitude: [11, 14] },
        { type: 'kinematic' },
        { type: 'fade', to: 0, tween: tweenPower2In },
      ],
    });
  }
}
